import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Mail, Smartphone } from "lucide-react";
import { AuthShell, Field } from "./AuthShell";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export function TwoFactorStep({ challenge, onSuccess, onCancel }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { verifyTwoFactor } = useAuth();
  const isEmail = challenge.method === "email";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verifyTwoFactor(challenge.challenge_token, code);
      onSuccess(u);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/2fa/resend", { challenge_token: challenge.challenge_token });
      toast.success("A new code has been sent");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setResending(false); }
  };

  return (
    <AuthShell title="Two-factor verification" subtitle={isEmail ? "Enter the 6-digit code we emailed you" : "Enter the code from your authenticator app"}
      footer={<button type="button" onClick={onCancel} className="text-cyan-400 hover:underline" data-testid="2fa-cancel">Back to login</button>}>
      <div className="glass rounded-xl p-3 mb-5 flex items-center gap-3 border-cyan-500/20" data-testid="2fa-step">
        {isEmail ? <Mail className="w-5 h-5 text-cyan-400" /> : <Smartphone className="w-5 h-5 text-cyan-400" />}
        <p className="text-xs text-slate-400">{isEmail ? "Codes expire after 10 minutes." : "Codes rotate every 30 seconds."}</p>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Verification code" testId="2fa-code" inputMode="numeric" autoComplete="one-time-code" required maxLength={8}
          value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" autoFocus />
        <button type="submit" disabled={loading || code.length < 6} data-testid="2fa-submit"
          className="btn-glow w-full py-3 rounded-lg font-semibold text-slate-950 inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Verify & Continue
        </button>
        {isEmail && (
          <button type="button" onClick={resend} disabled={resending} data-testid="2fa-resend" className="w-full text-xs text-slate-400 hover:text-cyan-300">
            {resending ? "Sending..." : "Didn't get it? Resend code"}
          </button>
        )}
      </form>
    </AuthShell>
  );
}
